/**
 * @requirement S-044 AC #4 — "Preference persisted in localStorage (Guest)
 *   or IndexedDB (signed-in)".
 *
 * Storage adapter for the user's theme preference. Guest sessions
 * write to `localStorage['recto:theme']`; a future signed-in branch
 * will swap to IndexedDB via the same interface. Reads are SSR-safe
 * (returns null when `window` is absent) so the pre-paint inline
 * script in index.html can share the storage key without pulling in
 * this module.
 */

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'recto:theme';

function isValidPreference(value: string | null): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function readPersistedTheme(): ThemePreference | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidPreference(raw) ? raw : null;
  } catch {
    // Access to localStorage can throw in private-mode Safari or when
    // storage quotas are exhausted. Treat as "no preference" and let
    // the caller fall back to system-following.
    return null;
  }
}

export function writePersistedTheme(preference: ThemePreference): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Same failure modes as readPersistedTheme. Silent fail is
    // acceptable — the theme is applied to the DOM immediately;
    // only the persistence across reloads is lost.
  }
}
